/**
 * Product × Channel
 *
 * Which products each channel sells, on the Channels report's attribution
 * engine: the same paid orders, journeys, models, lookback and attribution
 * window, so a channel's column here adds up to its row on Channels. Products
 * are named as on the Products page — molecule, then supply length from
 * program_definition, then plan. See services/attribution/productCredit.
 */

import { Fragment, useEffect, useMemo, useState } from 'react'
import { format, subDays } from 'date-fns'
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react'
import { dailyReportAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { DateRangePicker } from '../components/DateRangePicker'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui'
import AttributionModelDetails from '../components/AttributionModelDetails'
import AttributionSettings from '../components/AttributionSettings'
import { ATTRIBUTION_CHANNEL_LABELS } from '../components/AttributionCreditTable'
import { channelColor, channelLabel } from '../lib/channels'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'


// Credit the engine could not place on a channel sits after the channels.
const TRAILING = ['outside_window', 'unattributed']

const labelOf = channel => ATTRIBUTION_CHANNEL_LABELS[channel] || channelLabel(channel)

function defaultRange() {
  const to = new Date()
  return { from: subDays(to, 29), to }
}

/** Orders are fractional under Linear; show a decimal only where it matters. */
function formatOrders(value) {
  if (!value) return '–'
  return value.toLocaleString('en-US', { maximumFractionDigits: value < 10 && !Number.isInteger(value) ? 1 : 0 })
}

/** A share as a percent, with "<1%" for a sliver rather than a false 0%. */
function formatShare(part, whole) {
  if (!part || !whole) return '–'
  const share = part / whole
  return share < 0.01 ? '<1%' : `${Math.round(share * 100)}%`
}

function formatDollars(cents) {
  if (!cents) return '–'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100)
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-md border p-0.5">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${value === option.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

const emptyTotals = () => ({ orders: 0, revenue: 0, byChannel: new Map() })

function addCell(target, cell) {
  target.orders += cell.orders
  target.revenue += cell.revenue
  const c = target.byChannel.get(cell.channel) || { orders: 0, revenue: 0 }
  c.orders += cell.orders
  c.revenue += cell.revenue
  target.byChannel.set(cell.channel, c)
}

/** Cells → molecule › supply › plan, each node carrying per-channel totals. */
function buildTree(cells) {
  const molecules = new Map()
  const columns = emptyTotals()
  for (const cell of cells) {
    addCell(columns, cell)
    if (!molecules.has(cell.molecule)) molecules.set(cell.molecule, { key: cell.molecule, label: cell.molecule, ...emptyTotals(), children: new Map() })
    const molecule = molecules.get(cell.molecule)
    addCell(molecule, cell)
    const supplyKey = `${cell.molecule}|${cell.supply}`
    if (!molecule.children.has(supplyKey)) molecule.children.set(supplyKey, { key: supplyKey, label: cell.supply, months: cell.supply_months, ...emptyTotals(), children: new Map() })
    const supply = molecule.children.get(supplyKey)
    addCell(supply, cell)
    const planKey = `${supplyKey}|${cell.plan}`
    if (!supply.children.has(planKey)) supply.children.set(planKey, { key: planKey, label: cell.plan, ...emptyTotals() })
    addCell(supply.children.get(planKey), cell)
  }
  const byOrders = (a, b) => b.orders - a.orders
  const rows = [...molecules.values()].sort(byOrders).map(m => ({
    ...m,
    children: [...m.children.values()]
      .sort((a, b) => (a.months ?? Infinity) - (b.months ?? Infinity))
      .map(s => ({ ...s, children: [...s.children.values()].sort(byOrders) })),
  }))
  const channels = [...columns.byChannel.entries()]
    .sort(([a, x], [b, y]) => (TRAILING.includes(a) - TRAILING.includes(b)) || y.orders - x.orders)
    .map(([channel]) => channel)
  return { rows, columns, channels }
}

export default function ProductByChannelPage() {
  const { currentClinicId } = useAuthStore()
  const [dateRange, setDateRange] = useState(defaultRange)
  const [engine, setEngine] = useState('last-non-direct')
  const [scope, setScope] = useState('new')
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page
  const [attributionWindow, setAttributionWindow] = useState('all')
  const [accountingMode, setAccountingMode] = useState('payment')
  const [measure, setMeasure] = useState('orders')
  const [mode, setMode] = useState('value')
  const [open, setOpen] = useState(() => new Set())
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  useEffect(() => {
    if (!currentClinicId) return
    let alive = true
    setLoading(true)
    setError(null)
    dailyReportAPI.getProductAttribution({ startDate, endDate, engine, scope, lookbackDays, attributionWindow, accountingMode })
      .then(r => { if (alive && r.data?.success) setData(r.data.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [currentClinicId, startDate, endDate, engine, scope, lookbackDays, attributionWindow, accountingMode, reloadKey])

  const tree = useMemo(() => buildTree(data?.cells || []), [data])
  const toggle = key => setOpen(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const topChannel = tree.channels.find(c => !TRAILING.includes(c))
  const topProduct = tree.rows
    .flatMap(molecule => molecule.children.map(supply => ({ ...supply, molecule: molecule.label })))
    .sort((a, b) => b.orders - a.orders)[0]
  const totalOf = node => node[measure]

  // A cell is its value, or its share of the channel's column: which products
  // each channel sells. Tinted in the channel's own color by that share.
  const cell = (node, channel) => {
    const value = node.byChannel.get(channel)?.[measure] || 0
    const column = tree.columns.byChannel.get(channel)?.[measure] || 0
    const share = column > 0 ? value / column : 0
    const text = !value ? '–'
      : mode === 'share' ? formatShare(value, column)
        : measure === 'revenue' ? formatDollars(value) : formatOrders(value)
    const tint = value && !TRAILING.includes(channel) ? Math.round(Math.min(1, share) * 45) : 0
    return (
      <td key={channel} className="px-3 py-2 text-right whitespace-nowrap"
        style={tint ? { background: `color-mix(in srgb, ${channelColor(channel)} ${tint}%, transparent)` } : undefined}
        title={`${labelOf(channel)} · ${node.label}: ${measure === 'revenue' ? formatDollars(value) : formatOrders(value)} (${Math.round(share * 100)}% of ${labelOf(channel)})`}>
        {text}
      </td>
    )
  }
  const totalCell = node => (
    <td className="px-3 py-2 text-right font-medium whitespace-nowrap border-l">
      {mode === 'share'
        ? formatShare(totalOf(node), totalOf(tree.columns))
        : measure === 'revenue' ? formatDollars(node.revenue) : formatOrders(node.orders)}
    </td>
  )

  const row = (node, depth, expandable) => {
    const isOpen = open.has(node.key)
    return (
      <tr key={node.key} className={`border-t hover:bg-muted/40 ${depth === 0 ? 'font-medium' : ''}`}>
        <th scope="row" className="sticky left-0 z-10 bg-background text-left font-normal px-3 py-2 min-w-56">
          <button type="button" disabled={!expandable} onClick={() => toggle(node.key)}
            className="flex items-center gap-1.5 text-left disabled:cursor-default" style={{ paddingLeft: depth * 18 }}>
            {expandable ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />) : <span className="w-3.5 shrink-0" />}
            <span className={depth === 2 ? 'font-mono text-xs text-muted-foreground' : depth === 0 ? 'font-semibold' : ''}>{node.label}</span>
          </button>
        </th>
        {tree.channels.map(channel => cell(node, channel))}
        {totalCell(node)}
      </tr>
    )
  }

  const reconciles = data?.model?.reconciles !== false

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Product × Channel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Which products each channel sells, on the Channels report&apos;s attribution engine. Eastern time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={range => { if (range?.from && range?.to) setDateRange(range) }} />
          <Button variant="outline" size="icon" onClick={() => setReloadKey(k => k + 1)} disabled={loading} className="h-9 w-9" aria-label="Refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-3 px-4">
          <AttributionSettings
            models={data?.models || [{ id: 'last-non-direct', label: 'Last Non-Direct Touch' }]}
            engine={engine} onEngineChange={setEngine}
            scope={scope} onScopeChange={setScope}
            accountingMode={accountingMode} onAccountingModeChange={setAccountingMode}
            attributionWindow={attributionWindow} onAttributionWindowChange={setAttributionWindow} />
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="py-4 flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" /><span>{error}</span>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Attributed orders', formatOrders(data.totals.orders)],
              ['Revenue', formatDollars(data.totals.revenue)],
              ['Top channel', topChannel ? labelOf(topChannel) : '–'],
              ['Top product', topProduct ? `${topProduct.molecule} · ${topProduct.label}` : '–'],
            ].map(([label, value]) => (
              <Card key={label}>
                <CardContent className="pt-4 pb-3">
                  <div className="text-xs text-muted-foreground font-medium mb-1">{label}</div>
                  <div className="text-xl font-bold truncate" title={value}>{value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Products by channel</CardTitle>
                  <CardDescription>
                    {data.model.label} · {scope === 'new' ? 'new orders' : 'all paid orders'}. Molecule › supply length › plan; shading is each product&apos;s share of its channel.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Segmented label="Measure" value={measure} onChange={setMeasure} options={[{ value: 'orders', label: 'Orders' }, { value: 'revenue', label: 'Revenue' }]} />
                  <Segmented label="Show" value={mode} onChange={setMode} options={[{ value: 'value', label: 'Total' }, { value: 'share', label: '% of channel' }]} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!reconciles && <p className="text-xs text-amber-700 dark:text-amber-400">{data.model.label} credits every channel a customer touched, so columns overlap and add up to more than actual orders.</p>}
              {data.bigquery_touches && !data.bigquery_touches.checked && <p className="text-xs text-amber-700 dark:text-amber-400">BigQuery clicks were not added ({data.bigquery_touches.reason}), so journeys show only what core recorded.</p>}
              {tree.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No attributed orders in these dates.</p>
              ) : (
                <div className={`overflow-auto max-h-[75vh] rounded-md border ${loading ? 'opacity-60' : ''}`}>
                  <table className="w-full text-sm tabular-nums">
                    <caption className="sr-only">Attributed {measure} by product and channel</caption>
                    <thead className="sticky top-0 z-20 bg-background">
                      <tr className="border-b">
                        <th scope="col" className="sticky left-0 z-30 bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground">Product</th>
                        {tree.channels.map(channel => (
                          <th key={channel} scope="col" className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5">
                              {!TRAILING.includes(channel) && <span className="inline-block h-2 w-2 rounded-full" style={{ background: channelColor(channel) }} />}
                              {labelOf(channel)}
                            </span>
                          </th>
                        ))}
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-muted-foreground border-l">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tree.rows.map(molecule => (
                        <Fragment key={molecule.key}>
                          {row(molecule, 0, true)}
                          {open.has(molecule.key) && molecule.children.map(supply => (
                            <Fragment key={supply.key}>
                              {row(supply, 1, supply.children.length > 0)}
                              {open.has(supply.key) && supply.children.map(plan => row(plan, 2, false))}
                            </Fragment>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 font-semibold bg-muted/30">
                        <th scope="row" className="sticky left-0 z-10 bg-muted px-3 py-2 text-left">Total</th>
                        {tree.channels.map(channel => {
                          const v = tree.columns.byChannel.get(channel)?.[measure] || 0
                          return <td key={channel} className="px-3 py-2 text-right whitespace-nowrap">
                            {mode === 'share' ? formatShare(v, totalOf(tree.columns)) : measure === 'revenue' ? formatDollars(v) : formatOrders(v)}
                          </td>
                        })}
                        {totalCell(tree.columns)}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              <details className="rounded-md border p-4 text-sm">
                <summary className="cursor-pointer text-xs text-muted-foreground">How this is attributed</summary>
                <div className="mt-3 space-y-2 text-muted-foreground">
                  <div className="flex flex-wrap gap-2"><AttributionModelDetails model={data.model} scope={scope} lookbackDays={lookbackDays} attributionWindow={attributionWindow} accountingMode={accountingMode} /></div>
                  <p>The engine is the Channels report&apos;s: the same paid orders, journeys, lookback and attribution window. Each channel&apos;s column adds up to that channel&apos;s row on Channels, including credit outside the attribution window and orders no journey could place.</p>
                  <p>Products are named as on the Products page: the molecule from the plan, and the supply length the plan sells, read from the clinic&apos;s program definitions.</p>
                  {scope === 'all' && <p>Renewals are credited only through recorded visits, so long-standing customers whose first visits predate tracking show as Unattributed.</p>}
                </div>
              </details>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
